// Column validation and normalization. A column declares its key and type; every other declaration has a
// default (config/defaults.js), and an explicit value is checked against the rest of the column.

import {
  COLUMN_TYPES,
  DEFAULT_LABELLED_ACTIONS_WIDTH,
  DEFAULT_MAX_WIDTH,
  DEFAULT_MIN_WIDTH,
  DEFAULT_WIDTHS,
  DEFAULT_WRAP,
  FILTER_TYPES,
  LOCK_EDGES,
  SUMMARY_AGGREGATES,
  WRAP_POLICIES,
  defaultFilterType,
} from './defaults'

export const isFunction = value => typeof value === 'function'
export const isText = value => typeof value === 'string' && value.trim() !== ''
export const isPositiveNumber = value =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
export const isPositiveInteger = value => Number.isInteger(value) && value > 0
const isBoolean = value => typeof value === 'boolean'
const declared = value => value !== undefined && value !== null

export const columnName = (column, index) =>
  `columns[${index}]${column && column.key ? ` (${column.key})` : ''}`

// The filter a column offers: `filterable: true` picks the filter for its type, or it names one.
export const filterTypeOf = column => {
  if (!column || !column.filterable) return null
  if (column.filterable === true) return defaultFilterType(column.type)
  return FILTER_TYPES.includes(column.filterable) ? column.filterable : null
}

const isAggregate = summary =>
  SUMMARY_AGGREGATES.includes(summary) || isFunction(summary)

const defaultWidth = column => {
  if (column.type === 'actions' && isText(column.actionsLabel))
    return DEFAULT_LABELLED_ACTIONS_WIDTH
  return DEFAULT_WIDTHS[column.type] || DEFAULT_WIDTHS.text
}

// A column whose value is text that wraps or truncates (a text, link or date column the table renders
// itself, without a nowrap policy) keeps a readable minimum; any other column's minimum is its content
// floor, measured in the browser (geometry/contentFloor.js).
export const keepsReadableMin = column =>
  ['text', 'link', 'date'].includes(column.type) &&
  column.wrap !== 'nowrap' &&
  !isFunction(column.render)

// Width bounds with defaults applied. Undeclared bounds widen to include a declared width, so declaring
// only `width` never fails the bounds check; an actions column is exactly as wide as its trigger. A data
// column's minimum is the larger of its declared minimum and, for a column that keeps one, the readable
// minimum; with neither it is 0, so the content floor alone (view.js effectiveMin) sets how narrow the
// column goes, and a declared minimum holds only where it is wider than that floor.
export const columnSize = column => {
  const actions = column.type === 'actions'
  const width = isPositiveNumber(column.width)
    ? column.width
    : defaultWidth(column)
  let min = isPositiveNumber(column.minWidth) ? column.minWidth : null
  let max = isPositiveNumber(column.maxWidth) ? column.maxWidth : null
  if (actions) {
    if (min === null) min = width
  } else {
    const readable = keepsReadableMin(column)
      ? Math.min(DEFAULT_MIN_WIDTH, width)
      : 0
    min = Math.max(min || 0, readable)
  }
  if (max === null) max = actions ? width : Math.max(DEFAULT_MAX_WIDTH, width)
  return { width, min, max }
}

const checkFunction = (column, field, name, errors) => {
  if (declared(column[field]) && !isFunction(column[field]))
    errors.push(`${name}: ${field} must be a function of the row`)
}

const checkBoolean = (column, field, name, errors) => {
  if (declared(column[field]) && !isBoolean(column[field]))
    errors.push(`${name}: ${field} must be true or false`)
}

const checkActionsColumn = (column, name, errors) => {
  ;[
    'sortable',
    'hideable',
    'resizable',
    'reorderable',
    'searchable',
    'filterable',
    'summary',
    'required',
  ].forEach(field => {
    if (column[field])
      errors.push(`${name}: an actions column cannot be ${field}`)
  })
  if (declared(column.lock) && column.lock !== 'end')
    errors.push(`${name}: an actions column locks to the end edge`)
  checkFunction(column, 'actions', name, errors)
  if (declared(column.actionsLabel) && !isText(column.actionsLabel))
    errors.push(`${name}: actionsLabel must be text`)
}

const checkOptions = (column, name, errors) => {
  const { options } = column
  if (!declared(options)) return
  if (!Array.isArray(options) || !options.length) {
    errors.push(`${name}: options must list at least one option`)
    return
  }
  if (!options.every(isText))
    errors.push(`${name}: every option must be non-empty text`)
  if (new Set(options).size !== options.length)
    errors.push(`${name}: options must be unique`)
}

const checkRefinement = (column, filter, name, errors) => {
  const { refinement } = column
  if (!declared(refinement)) return
  if (filter !== 'enum') {
    errors.push(`${name}: only an enumerable filter can declare a refinement`)
    return
  }
  if (typeof refinement !== 'object') {
    errors.push(`${name}: refinement must declare option, label and test`)
    return
  }
  if (!isText(refinement.option))
    errors.push(`${name}: refinement.option must name one option`)
  else if (
    Array.isArray(column.options) &&
    !column.options.includes(refinement.option)
  )
    errors.push(
      `${name}: refinement.option ${refinement.option} is not one of the filter's options`
    )
  if (!isText(refinement.label))
    errors.push(`${name}: refinement.label is required`)
  if (!isFunction(refinement.test))
    errors.push(`${name}: refinement.test must be a function of the row`)
}

const checkFilter = (column, name, sourceMode, errors) => {
  if (!column.filterable) return null
  if (column.type === 'text') {
    errors.push(
      `${name}: a text column cannot be filterable; mark it searchable instead`
    )
    return null
  }
  const filter = filterTypeOf(column)
  if (!filter) {
    errors.push(
      `${name}: filterable must be true or one of ${FILTER_TYPES.join(', ')}`
    )
    return null
  }
  if (filter === 'numeric' && column.type !== 'numeric')
    errors.push(`${name}: a numeric filter needs a numeric column`)
  if (filter === 'date' && column.type !== 'date')
    errors.push(`${name}: a date filter needs a date column`)
  if (filter === 'enum' && sourceMode === 'page' && !declared(column.options))
    errors.push(
      `${name}: a page-backed enumerable filter must declare its options`
    )
  return filter
}

// A custom renderer declares a plain value for each capability it takes part in.
const checkRenderer = (column, filter, name, errors) => {
  if (!column.render) return
  if (!isFunction(column.render)) {
    errors.push(`${name}: render must be a function of the row`)
    return
  }
  if (column.searchable && !column.searchText)
    errors.push(`${name}: a searchable custom renderer must declare searchText`)
  if (column.sortable && !column.value)
    errors.push(
      `${name}: a sortable custom renderer must declare its sort value`
    )
  if (filter && !column.value)
    errors.push(
      `${name}: a filterable custom renderer must declare its filter value`
    )
  if (column.summary && !column.value)
    errors.push(
      `${name}: a summarized custom renderer must declare its summary value`
    )
  if (
    column.type !== 'actions' &&
    column.exportable !== false &&
    !column.value &&
    !column.exportValue
  )
    errors.push(
      `${name}: a custom renderer must declare value or exportValue for export, or set exportable: false`
    )
}

const checkSize = (column, name, errors) => {
  ;['width', 'minWidth', 'maxWidth'].forEach(field => {
    if (declared(column[field]) && !isPositiveNumber(column[field]))
      errors.push(`${name}: ${field} must be a positive number of pixels`)
  })
  const { width, min, max } = columnSize(column)
  if (min > max) {
    errors.push(`${name}: minWidth cannot exceed maxWidth`)
    return
  }
  const resizable = column.type !== 'actions' && column.resizable !== false
  if (resizable && (width < min || width > max))
    errors.push(`${name}: width must fall within minWidth and maxWidth`)
}

const checkPlacement = (column, name, errors) => {
  if (declared(column.wrap) && !WRAP_POLICIES.includes(column.wrap))
    errors.push(`${name}: wrap must be one of ${WRAP_POLICIES.join(', ')}`)
  if (declared(column.lock) && !LOCK_EDGES.includes(column.lock))
    errors.push(`${name}: lock must be one of ${LOCK_EDGES.join(', ')}`)
  ;['visible', 'hideable', 'reorderable', 'resizable', 'exportable'].forEach(
    field => checkBoolean(column, field, name, errors)
  )
  if (column.required) {
    if (column.visible === false)
      errors.push(`${name}: a required column is always visible`)
    if (column.hideable)
      errors.push(`${name}: a required column cannot be hideable`)
    if (column.reorderable)
      errors.push(`${name}: a required column cannot be reordered`)
  }
  if (declared(column.lock) && column.reorderable)
    errors.push(`${name}: a locked column cannot be reordered`)
}

const checkSortOrder = (column, name, errors) => {
  const { sortOrder } = column
  if (!declared(sortOrder)) return
  if (!Array.isArray(sortOrder) || !sortOrder.length)
    errors.push(`${name}: sortOrder must list at least one value`)
  else if (new Set(sortOrder).size !== sortOrder.length)
    errors.push(`${name}: sortOrder values must be unique`)
}

const checkSummary = (column, name, errors) => {
  if (!declared(column.summary) || column.summary === false) return
  if (!isAggregate(column.summary))
    errors.push(
      `${name}: summary must be 'sum' or a function of the matching rows`
    )
  else if (column.summary === 'sum' && column.type !== 'numeric')
    errors.push(`${name}: only numeric columns can be summed`)
}

/**
 * Every error one column carries. `keys` collects keys across columns to find duplicates.
 * @returns {string[]}
 */
export const validateColumn = (column, index, { keys, sourceMode }) => {
  const errors = []
  const name = columnName(column, index)
  if (!column || typeof column !== 'object') return [`${name}: missing`]
  if (!isText(column.key)) return [`${name}: key is required`]
  if (keys.has(column.key)) errors.push(`${name}: duplicate key`)
  keys.add(column.key)

  if (!COLUMN_TYPES.includes(column.type)) {
    errors.push(`${name}: type must be one of ${COLUMN_TYPES.join(', ')}`)
    return errors
  }
  if (column.type === 'actions') {
    checkActionsColumn(column, name, errors)
  } else if (!isText(column.label)) {
    errors.push(`${name}: label is required as visible header text`)
  }

  const filter =
    column.type === 'actions'
      ? null
      : checkFilter(column, name, sourceMode, errors)
  checkOptions(column, name, errors)
  checkRefinement(column, filter, name, errors)
  checkRenderer(column, filter, name, errors)
  ;['value', 'searchText', 'exportValue'].forEach(field =>
    checkFunction(column, field, name, errors)
  )
  checkSize(column, name, errors)
  checkPlacement(column, name, errors)
  checkSortOrder(column, name, errors)
  checkSummary(column, name, errors)
  if (declared(column.minMenuWidth) && !isPositiveNumber(column.minMenuWidth))
    errors.push(`${name}: minMenuWidth must be a positive number of pixels`)
  return errors
}

const exportValueOf = column => {
  if (column.type === 'actions' || column.exportable === false) return null
  if (isFunction(column.exportValue)) return column.exportValue
  if (isFunction(column.value)) return column.value
  if (column.render) return null
  return row => (row ? row[column.key] : undefined)
}

const refinementOf = column => {
  const { refinement } = column
  if (!refinement || typeof refinement !== 'object') return null
  if (!isText(refinement.option) || !isFunction(refinement.test)) return null
  return {
    option: refinement.option,
    label: refinement.label,
    test: refinement.test,
  }
}

/**
 * The shape every module reads (types.js NormalizedColumn). Never throws on an invalid column.
 * @param {object} column
 * @param {{minMenuWidth: number}} instance
 */
export const normalizeColumn = (column, instance) => {
  const actions = column.type === 'actions'
  const required = Boolean(column.required) && !actions
  let lock = LOCK_EDGES.includes(column.lock) ? column.lock : null
  if (actions) lock = 'end'
  const filter = actions || column.type === 'text' ? null : filterTypeOf(column)
  return {
    ...column,
    label: isText(column.label) ? column.label : '',
    required,
    wrap: WRAP_POLICIES.includes(column.wrap) ? column.wrap : DEFAULT_WRAP,
    capabilities: {
      search: Boolean(column.searchable) && !actions,
      filter,
      sort: Boolean(column.sortable) && !actions,
      summary: isAggregate(column.summary) ? column.summary : null,
      export: exportValueOf(column),
    },
    lock,
    visible: required || actions ? true : column.visible !== false,
    hideable:
      required || actions
        ? false
        : isBoolean(column.hideable)
        ? column.hideable
        : lock === null,
    reorderable:
      required || actions || lock !== null
        ? false
        : column.reorderable !== false,
    resizable: !actions && column.resizable !== false,
    size: columnSize(column),
    refinement: refinementOf(column),
    minMenuWidth: isPositiveNumber(column.minMenuWidth)
      ? column.minMenuWidth
      : instance.minMenuWidth,
  }
}
